import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, CheckCircle2, Settings as SettingsIcon, Globe, Code2, Link2, Unlink } from "lucide-react";

const idr = (n) => "Rp " + Math.round(parseFloat(n || 0)).toLocaleString("id-ID");

// Sama seperti getCurrentPeriode() di ProjectPayment.jsx — tanggal 1-15 default ke periode "15",
// 16-akhir bulan default ke periode "30".
const getCurrentPeriode = () => {
  const today = new Date().getDate();
  return today <= 15 ? "15" : "30";
};

export default function PaymentDeveloper() {
  const [tab, setTab] = useState("rekap"); // rekap, settings
  const [recap, setRecap] = useState([]);
  const [devs, setDevs] = useState([]);
  const [types, setTypes] = useState([]);
  const [exts, setExts] = useState([]);
  const [webRates, setWebRates] = useState([]);
  const [domRates, setDomRates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().slice(0, 10);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: today, amount_paid: 0, metode: "Transfer", catatan: "" });
  const [detail, setDetail] = useState(null);

  // Filter periode Rekap & Bayar — SAMA seperti filter periode di Payment Website (ProjectPayment.jsx):
  // periode "15" (due 1-15) / "30" (due 16-30/31) / "all" (semua), digabung filter bulan (YYYY-MM).
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));
  const [filterPeriode, setFilterPeriode] = useState(getCurrentPeriode);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d, t, e, wr, dr, emp] = await Promise.all([
        api.get("/dev-payment/recap", { params: { bulan: filterBulan, periode: filterPeriode } }),
        api.get("/developers"), api.get("/web-types"), api.get("/domain-ext"),
        api.get("/dev-web-rates"), api.get("/dev-domain-rates"), api.get("/employees"),
      ]);
      setRecap(r.data); setDevs(d.data); setTypes(t.data); setExts(e.data);
      setWebRates(wr.data); setDomRates(dr.data); setEmployees(emp.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterPeriode]);

  const submitPay = async () => {
    try {
      await api.post("/dev-payment", {
        developer_id: payTarget.developer_id, periode: payTarget.periode,
        payment_date: payForm.payment_date, amount_paid: parseFloat(payForm.amount_paid || 0),
        metode: payForm.metode, catatan: payForm.catatan,
      });
      toast.success("Pembayaran developer tercatat (arus kas keluar otomatis)");
      setPayTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const totalEarning = recap.reduce((s, r) => s + (r.total_earning_idr || 0), 0);
  const totalPaid = recap.reduce((s, r) => s + (r.paid_idr || 0), 0);
  const totalSisa = recap.reduce((s, r) => s + (r.sisa_idr || 0), 0);

  return (
    <Window
      title="Payment Developer (IDR)"
      actions={
        <>
          <button className={`vb-btn ${tab === "rekap" ? "vb-btn-primary" : ""}`} onClick={() => setTab("rekap")} data-testid="tab-dev-rekap">Rekap & Bayar</button>
          <button className={`vb-btn ${tab === "settings" ? "vb-btn-primary" : ""}`} onClick={() => setTab("settings")} data-testid="tab-dev-settings"><SettingsIcon className="w-3 h-3 inline mr-1" />Pengaturan Rate</button>
        </>
      }
    >
      {tab === "rekap" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <select
              className="vb-select"
              style={{ width: 140 }}
              value={filterPeriode}
              onChange={(e) => setFilterPeriode(e.target.value)}
              data-testid="dev-filter-periode"
            >
              <option value="15">Periode 1-15</option>
              <option value="30">Periode 16-30/31</option>
              <option value="all">Semua Periode</option>
            </select>
            <input
              type="month"
              className="vb-input"
              style={{ width: 160 }}
              value={filterBulan}
              onChange={(e) => setFilterBulan(e.target.value)}
              data-testid="dev-filter-bulan"
              title="Filter bulan berdasarkan Due Date project (sama seperti Payment Website) — tetap berlaku walau periode dipilih 'Semua Periode'"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div className="vb-statcard"><div className="vb-statcard-label">Total Earning Dev</div><div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{idr(totalEarning)}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Sudah Dibayar</div><div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 16 }}>{idr(totalPaid)}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Belum Dibayar</div><div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 16 }}>{idr(totalSisa)}</div></div>
          </div>

          {loading ? <Loading /> : recap.length === 0 ? <EmptyState message="Belum ada project terhubung developer pada periode/bulan ini. Coba ganti filter periode, atau tambahkan project pada menu Payment Website lalu set rate developer di tab Pengaturan." /> : (
            <div style={{ overflowX: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Periode (Due 15/30)</th>
                    <th>Developer</th>
                    <th style={{ textAlign: "center" }}>Jml Project</th>
                    <th style={{ textAlign: "right" }}>Total Earning</th>
                    <th style={{ textAlign: "right" }}>Dibayar</th>
                    <th style={{ textAlign: "right" }}>Sisa</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.map((r) => (
                    <tr key={`${r.developer_id}|${r.periode}`}>
                      <td style={{ fontWeight: 700, color: "#003399" }}>{r.periode}</td>
                      <td><strong>{r.developer_nama}</strong></td>
                      <td style={{ textAlign: "center" }}>
                        <button className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setDetail(r)} data-testid={`dev-detail-${r.developer_id}-${r.periode}`}>{r.count} ▾</button>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {idr(r.total_earning_idr)}
                        {r.total_earning_gross_idr > r.total_earning_idr && (
                          <div style={{ fontSize: 10, color: "#888" }}>bruto: {idr(r.total_earning_gross_idr)}</div>
                        )}
                      </td>
                      <td style={{ textAlign: "right", color: "#4f8a10" }}>{idr(r.paid_idr)}</td>
                      <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 600 }}>{idr(r.sisa_idr)}</td>
                      <td>
                        {r.status === "Lunas" ? <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                          : r.status === "Sebagian" ? <span className="vb-badge vb-badge-pending">Sebagian</span>
                          : <span className="vb-badge vb-badge-rejected">Pending</span>}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {r.status !== "Lunas" && (
                          <button className="vb-btn-primary" style={{ padding: "2px 6px" }} onClick={() => { setPayTarget(r); setPayForm({ payment_date: today, amount_paid: r.sisa_idr, metode: "Transfer", catatan: `Pembayaran periode ${r.periode}` }); }} data-testid={`dev-pay-${r.developer_id}-${r.periode}`}>
                            <Wallet className="w-3 h-3 inline mr-1" />Bayar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "settings" && (
        <>
          <DeveloperEmployeeLinkCard devs={devs} employees={employees} reload={load} />
          <RateSettings devs={devs} types={types} exts={exts} webRates={webRates} domRates={domRates} reload={load} />
        </>
      )}

      {/* Payment Dialog */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} title="Bayar Developer" width={460}
        footer={<><button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={submitPay} data-testid="dev-pay-save">Bayarkan</button></>}>
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginBottom: 12 }}>
              <strong>{payTarget.developer_nama}</strong> · Periode {payTarget.periode}<br />
              Total: {idr(payTarget.total_earning_idr)} &middot; Dibayar: {idr(payTarget.paid_idr)} &middot; <strong style={{ color: "#d8000c" }}>Sisa: {idr(payTarget.sisa_idr)}</strong>
            </div>
            <div className="vb-form-row"><label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Jumlah (IDR)*</label>
              <input type="number" className="vb-input" value={payForm.amount_paid} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} data-testid="dev-pay-amount" /></div>
            <div className="vb-form-row"><label className="vb-label">Metode</label>
              <select className="vb-select" value={payForm.metode} onChange={(e) => setPayForm({ ...payForm, metode: e.target.value })}>
                <option>Transfer</option><option>Cash</option>
              </select></div>
            <div className="vb-form-row"><label className="vb-label">Catatan</label>
              <textarea rows={2} className="vb-textarea" value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} /></div>
          </>
        )}
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={`Detail Project — ${detail?.developer_nama || ""} (${detail?.periode || ""})`} width={620}
        footer={<button className="vb-btn" onClick={() => setDetail(null)}>Tutup</button>}>
        {detail && (
          <table className="vb-table">
            <thead><tr><th>Project</th><th>Domain</th><th>Type</th><th>Ext</th><th style={{ textAlign: "right" }}>Rate Web</th><th style={{ textAlign: "right" }}>Rate Domain</th><th>Rating</th><th style={{ textAlign: "right" }}>Earning</th></tr></thead>
            <tbody>
              {detail.projects.map((p, i) => {
                const rEff = p.rating_effective ?? 5;
                const pct = p.pay_percent ?? 100;
                return (
                  <tr key={i}>
                    <td><strong>{p.nama_project}</strong></td>
                    <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                    <td>{p.type_web}</td>
                    <td><span className="vb-badge vb-badge-izin">{p.ext}</span></td>
                    <td style={{ textAlign: "right" }}>{idr(p.rate_web_idr)}</td>
                    <td style={{ textAlign: "right" }}>{idr(p.rate_domain_idr)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span style={{ color: "#f5a623", letterSpacing: -1 }}>{"★".repeat(rEff)}</span>
                      <span style={{ color: "#ddd", letterSpacing: -1 }}>{"★".repeat(5 - rEff)}</span>
                      {pct < 100 && <div style={{ color: "#7c3aed", fontWeight: 700, fontSize: 10 }}>{pct}%</div>}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#003399" }}>
                      {idr(p.earning_idr)}
                      {pct < 100 && (
                        <div style={{ fontSize: 10, color: "#888", textDecoration: "line-through" }}>{idr(p.earning_idr_gross)}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Dialog>
    </Window>
  );
}

// Delegasi ke akun KARYAWAN yang sudah ada (bukan akun login terpisah) — developer yang
// merupakan karyawan Fivehoster otomatis dapat menu "Pendapatan → List Web Project" di
// dashboard karyawan-nya sendiri begitu Developer.employee_id diisi di sini. Menyimpan lewat
// PUT /developers/{id} yang sudah ada (DeveloperIn sekarang punya field employee_id).
function DeveloperEmployeeLinkCard({ devs, employees, reload }) {
  const [linkTarget, setLinkTarget] = useState(null);
  const [empId, setEmpId] = useState("");
  const empName = (id) => employees.find((e) => e.id === id)?.nama_lengkap || "-";

  const doSave = async () => {
    try {
      await api.put(`/developers/${linkTarget.id}`, {
        nama: linkTarget.nama, kontak: linkTarget.kontak || "", email: linkTarget.email || "",
        employee_id: empId || null,
      });
      toast.success(empId ? `Developer "${linkTarget.nama}" terhubung ke karyawan ${empName(empId)}.` : `Tautan karyawan untuk "${linkTarget.nama}" dilepas.`);
      setLinkTarget(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Link2 className="w-3.5 h-3.5" style={{ color: "#003399" }} />
        <h3 className="vb-h1" style={{ margin: 0 }}>Delegasi ke Karyawan</h3>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
        Hubungkan developer ke akun karyawan yang sudah ada — karyawan tersebut otomatis dapat menu "Pendapatan → List Web Project" di dashboard karyawan-nya sendiri (login tetap sebagai karyawan biasa, tidak ada akun terpisah).
      </div>
      {devs.length === 0 ? <EmptyState message="Belum ada developer. Tambahkan dulu di Payment Website → Pengaturan → Developer." /> : (
        <table className="vb-table">
          <thead><tr><th>Developer</th><th>Karyawan Terhubung</th><th>Aksi</th></tr></thead>
          <tbody>
            {devs.map((d) => (
              <tr key={d.id}>
                <td><strong>{d.nama}</strong></td>
                <td>{d.employee_nama ? <span className="vb-badge vb-badge-approved">{d.employee_nama}</span> : <span className="vb-badge vb-badge-rejected">Belum</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => { setLinkTarget(d); setEmpId(d.employee_id || ""); }} data-testid={`dev-link-${d.id}`}>
                    {d.employee_id ? <><Link2 className="w-3 h-3 inline mr-1" />Ubah</> : <><Link2 className="w-3 h-3 inline mr-1" />Hubungkan</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!linkTarget} onClose={() => setLinkTarget(null)} title={`Hubungkan Developer ke Karyawan: ${linkTarget?.nama || ""}`} width={460}
        footer={<><button className="vb-btn" onClick={() => setLinkTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={doSave} data-testid="dev-link-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Karyawan</label>
          <select className="vb-select" value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="dev-link-employee">
            <option value="">- tidak terhubung -</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.nama_lengkap}</option>)}
          </select>
        </div>
        {empId && (
          <div style={{ fontSize: 11, color: "#666" }}>
            <Unlink className="w-3 h-3 inline mr-1" />Pilih "- tidak terhubung -" untuk melepas tautan (menu Pendapatan karyawan ybs akan hilang lagi).
          </div>
        )}
      </Dialog>
    </div>
  );
}

function RateSettings({ devs, types, exts, webRates, domRates, reload }) {
  const [section, setSection] = useState("web");
  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: "1px solid #828790" }}>
        <button onClick={() => setSection("web")} className={`vb-btn ${section === "web" ? "vb-btn-primary" : ""}`} style={{ borderRadius: "3px 3px 0 0" }} data-testid="dev-set-web"><Code2 className="w-3 h-3 inline mr-1" />Rate Web (per Type)</button>
        <button onClick={() => setSection("dom")} className={`vb-btn ${section === "dom" ? "vb-btn-primary" : ""}`} style={{ borderRadius: "3px 3px 0 0" }} data-testid="dev-set-dom"><Globe className="w-3 h-3 inline mr-1" />Rate Domain (per Ext)</button>
      </div>
      {section === "web" && (
        <RateTable endpoint="/dev-web-rates" items={webRates} reload={reload} devs={devs}
          optionKey="type_web" optionLabel="Type Web" options={types.map((t) => t.nama)} />
      )}
      {section === "dom" && (
        <RateTable endpoint="/dev-domain-rates" items={domRates} reload={reload} devs={devs}
          optionKey="ext" optionLabel="Ekstensi Domain" options={exts.map((e) => e.ext)} />
      )}
    </div>
  );
}

function RateTable({ endpoint, items, reload, devs, optionKey, optionLabel, options }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ developer_id: "", [optionKey]: "", rate_idr: 0 });
  const [del, setDel] = useState(null);
  const devName = (id) => devs.find((d) => d.id === id)?.nama || "-";

  const submit = async () => {
    try {
      await api.post(endpoint, { developer_id: form.developer_id, [optionKey]: form[optionKey], rate_idr: parseFloat(form.rate_idr || 0) });
      toast.success("Rate tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>Rate {optionLabel} per Developer (IDR)</h3>
        <button className="vb-btn-primary" onClick={() => { setForm({ developer_id: "", [optionKey]: "", rate_idr: 0 }); setOpen(true); }} data-testid={`add-rate-${optionKey}`}><Plus className="w-3 h-3 inline mr-1" />Tambah / Update Rate</button>
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada rate. Set rate agar earning developer terhitung." /> : (
        <table className="vb-table">
          <thead><tr><th>Developer</th><th>{optionLabel}</th><th style={{ textAlign: "right" }}>Rate (IDR)</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td><strong>{devName(it.developer_id)}</strong></td>
                <td>{it[optionKey]}</td>
                <td style={{ textAlign: "right", fontWeight: 600, color: "#003399" }}>{idr(it.rate_idr)}</td>
                <td><button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={`Set Rate ${optionLabel}`}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} disabled={!form.developer_id || !form[optionKey]} data-testid="rate-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Developer*</label>
          <select className="vb-select" value={form.developer_id} onChange={(e) => setForm({ ...form, developer_id: e.target.value })} data-testid="rate-dev">
            <option value="">- pilih developer -</option>
            {devs.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">{optionLabel}*</label>
          <select className="vb-select" value={form[optionKey]} onChange={(e) => setForm({ ...form, [optionKey]: e.target.value })} data-testid="rate-option">
            <option value="">- pilih -</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">Rate (IDR)*</label>
          <input type="number" className="vb-input" value={form.rate_idr} onChange={(e) => setForm({ ...form, rate_idr: e.target.value })} data-testid="rate-idr" /></div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message="Hapus rate ini?" />
    </div>
  );
}
